import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Typography,
  Card,
  Alert,
  Spin,
  Divider,
  Space,
  Tag,
} from "antd";
import {
  FileTextOutlined,
  SoundOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { apiRequest } from "@vojomo-core/shared";

const { Title, Text, Paragraph } = Typography;

interface AlignmentReport {
  _id: string;
  text: string;
  contributor_count: number;
  generated_at: string;
}

interface Podcast {
  _id: string;
  file_name: string;
  generated_at: string;
}

export function CoordinatorStudio(): React.ReactElement {
  const [report, setReport] = useState<AlignmentReport | null>(null);
  const [podcast, setPodcast] = useState<Podcast | null>(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [podcastLoading, setPodcastLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [generatingPodcast, setGeneratingPodcast] = useState(false);
  const [podcastUrl, setPodcastUrl] = useState<string | null>(null);
  const [podcastUrlLoading, setPodcastUrlLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [podcastError, setPodcastError] = useState<string | null>(null);

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const data = await apiRequest<AlignmentReport | null>({
        subRoute: "studio",
        path: "/reports/latest",
      });
      setReport(data);
    } finally {
      setReportLoading(false);
    }
  }, []);

  const loadPodcast = useCallback(async () => {
    setPodcastLoading(true);
    try {
      const data = await apiRequest<Podcast | null>({
        subRoute: "studio",
        path: "/podcasts/latest",
      });
      setPodcast(data);
    } finally {
      setPodcastLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
    loadPodcast();
  }, [loadReport, loadPodcast]);

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setReportError(null);
    try {
      const data = await apiRequest<AlignmentReport>({
        method: "POST",
        subRoute: "studio",
        path: "/reports/generate",
      });
      setReport(data);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to generate report. Check that team members have submitted QBR scores.";
      setReportError(message);
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleGeneratePodcast = async () => {
    setGeneratingPodcast(true);
    setPodcastError(null);
    setPodcastUrl(null);
    try {
      const data = await apiRequest<Podcast>({
        method: "POST",
        subRoute: "studio",
        path: "/podcasts/generate",
      });
      setPodcast(data);
    } catch {
      setPodcastError("Failed to generate podcast. Generate an alignment report first.");
    } finally {
      setGeneratingPodcast(false);
    }
  };

  const handlePlayPodcast = async () => {
    if (!podcast) return;
    setPodcastUrlLoading(true);
    try {
      const url = await apiRequest<string>({
        subRoute: "studio",
        path: `/podcasts/${podcast._id}/url`,
      });
      setPodcastUrl(url);
    } catch {
      setPodcastError("Failed to load audio. Please try again.");
    } finally {
      setPodcastUrlLoading(false);
    }
  };

  return (
    <div style={{ padding: "24px 0", maxWidth: 800 }}>
      <Title level={4} style={{ marginBottom: 4 }}>
        Studio
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 32 }}>
        AI-generated artifacts from your QBR scoring data.
      </Text>

      {/* ── Alignment Report ─────────────────────────────────── */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>Scoring Alignment Report</span>
          </Space>
        }
        extra={
          <Button
            icon={generatingReport ? <Spin size="small" /> : <ReloadOutlined />}
            onClick={handleGenerateReport}
            loading={generatingReport}
            disabled={generatingReport}
          >
            {report ? "Regenerate" : "Generate Report"}
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        {reportError && (
          <Alert
            type="error"
            message={reportError}
            closable
            onClose={() => setReportError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        {reportLoading ? (
          <div style={{ textAlign: "center", padding: 32 }}>
            <Spin />
          </div>
        ) : report ? (
          <>
            <Space style={{ marginBottom: 16 }}>
              <Tag color="blue">{report.contributor_count} contributors</Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Generated {new Date(report.generated_at).toLocaleString()}
              </Text>
            </Space>
            <Divider style={{ margin: "12px 0" }} />
            <Paragraph
              style={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.8,
                fontSize: 14,
              }}
            >
              {report.text}
            </Paragraph>
          </>
        ) : (
          <Text type="secondary">
            No report generated yet. Click "Generate Report" to synthesize the
            team's QBR scoring data into an alignment analysis.
          </Text>
        )}
      </Card>

      {/* ── AI Podcast ───────────────────────────────────────── */}
      <Card
        title={
          <Space>
            <SoundOutlined />
            <span>QBR Alignment Podcast</span>
          </Space>
        }
        extra={
          <Button
            icon={generatingPodcast ? <Spin size="small" /> : <SoundOutlined />}
            onClick={handleGeneratePodcast}
            loading={generatingPodcast}
            disabled={generatingPodcast || !report}
            title={!report ? "Generate an alignment report first" : undefined}
          >
            {podcast ? "Regenerate Podcast" : "Generate Podcast"}
          </Button>
        }
      >
        {podcastError && (
          <Alert
            type="error"
            message={podcastError}
            closable
            onClose={() => setPodcastError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        {podcastLoading ? (
          <div style={{ textAlign: "center", padding: 32 }}>
            <Spin />
          </div>
        ) : podcast ? (
          <>
            <Space style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Generated {new Date(podcast.generated_at).toLocaleString()}
              </Text>
            </Space>
            <Divider style={{ margin: "12px 0" }} />
            {podcastUrl ? (
              <audio
                controls
                src={podcastUrl}
                style={{ width: "100%" }}
                autoPlay={false}
              />
            ) : (
              <Button
                icon={<SoundOutlined />}
                onClick={handlePlayPodcast}
                loading={podcastUrlLoading}
              >
                Load & Play Podcast
              </Button>
            )}
          </>
        ) : (
          <Text type="secondary">
            {report
              ? 'No podcast generated yet. Click "Generate Podcast" to create an audio version of the alignment report.'
              : "Generate an alignment report first, then create a podcast from it."}
          </Text>
        )}
      </Card>
    </div>
  );
}
